// ---------------------------------------------------------------------------
// Combines the 6 indicators (indicators.ts) into a single value ALWAYS
// meant to be maximized — CalEvBasic's own resolution of "some indicators
// are better when higher, some are better when lower" (AlgoritmoCalculo.vb's
// `OFCalculation`/`PreProcess`, the `Select Case fp.Key` blocks): indicators
// 1-4 (Nash, log-Nash, Pearson, Symmetry — higher is better) are ADDED;
// indicators 5-6 (Pbias, RSR — lower is better) are SUBTRACTED, which
// flips their direction into the same "more is better" convention as 1-4.
// This is the exact piece of logic the user asked to preserve when this
// feature was scoped down from the full desktop tool (see
// project_evalhid_calibracion.md) — simplifying which indicators the UI
// exposes doesn't change how they'd combine if more than one were weighted.
//
// `weights` only needs entries for the indicators actually in use — Phase 2
// has no UI yet, so the caller (Phase 3's calibration panel) decides which
// ones to expose; a single-indicator default of `{1: 1}` (NSE only) matches
// the "reduce the user's options" simplification the user asked for.
// ---------------------------------------------------------------------------

import {
  logNash,
  nash,
  pearson,
  percentBias,
  rsr,
  symmetry,
  type IndicatorWindow,
} from "./indicators.ts";

export type IndicatorCode = 1 | 2 | 3 | 4 | 5 | 6;

/** Indicators 1-4 add to the objective function (higher raw value = better); 5-6 subtract (lower raw value = better). */
const ADDED_INDICATORS: readonly IndicatorCode[] = [1, 2, 3, 4];

function computeIndicator(
  code: IndicatorCode,
  observed: number[],
  simulated: number[],
  window: IndicatorWindow
): number {
  switch (code) {
    case 1:
      return nash(observed, simulated, window);
    case 2:
      return logNash(observed, simulated, window);
    case 3:
      return pearson(observed, simulated, window);
    case 4:
      return symmetry(observed, simulated, window);
    case 5:
      return percentBias(observed, simulated, window);
    case 6:
      return rsr(observed, simulated, window);
  }
}

interface ObjectiveFunctionResult {
  /** Always meant to be maximized. */
  value: number;
  /** Each indicator's own raw value (unweighted), for the calibration report. */
  indicators: Partial<Record<IndicatorCode, number>>;
}

export function objectiveFunction(
  weights: Partial<Record<IndicatorCode, number>>,
  observed: number[],
  simulated: number[],
  window: IndicatorWindow
): ObjectiveFunctionResult {
  let value = 0;
  const indicators: Partial<Record<IndicatorCode, number>> = {};
  for (const [codeKey, weight] of Object.entries(weights)) {
    if (!weight) continue;
    const code = Number(codeKey) as IndicatorCode;
    const raw = computeIndicator(code, observed, simulated, window);
    indicators[code] = raw;
    value += ADDED_INDICATORS.includes(code) ? weight * raw : -weight * raw;
  }
  return { value, indicators };
}
